import { useTranslation } from 'react-i18next';

/*
 * A live event's leaderboards (routes/event_cards.py scoreboard): teams and
 * players by Research score as progress bars, the best single find, and the
 * Crowd Favorite -- the finds most people outside their author's team
 * reacted to or answered in the event's chat.
 */
export default function EventBoard({ board, me, cardsById = {} }) {
  const { t } = useTranslation('events');
  if (!board) return <p style={{ padding: 12 }}>{t('board.waiting')}</p>;
  const top = Math.max(1, ...board.players.map((p) => p.score), ...board.teams.map((x) => x.score));
  const bar = (score, colour) => (
    <span style={bs.track} aria-hidden="true">
      <span style={{ ...bs.fill, width: `${Math.round((score / top) * 100)}%`, background: colour || 'var(--c-primary)' }} />
    </span>
  );
  return (
    <div data-testid="event-board" style={{ display: 'grid', gap: 16 }}>
      {board.teams.length > 0 && (
        <section aria-labelledby="board-teams">
          <h3 id="board-teams" style={bs.h}>{t('board.teams')}</h3>
          <ol style={bs.list}>
            {board.teams.map((team) => (
              <li key={team.team_id} style={bs.row} data-testid="event-board-team">
                <span style={bs.label} dir="auto">{team.name}</span>
                {bar(team.score, team.colour)}
                <span style={bs.value}>{Number(team.score).toFixed(1)}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
      <section aria-labelledby="board-players">
        <h3 id="board-players" style={bs.h}>{t('board.players')}</h3>
        {board.players.length === 0 && <p>{t('board.nobody')}</p>}
        <ol style={bs.list}>
          {board.players.map((p) => (
            <li key={p.user_id} style={{ ...bs.row, fontWeight: p.user_id === me ? 700 : 400 }}
              data-testid="event-board-player" data-me={p.user_id === me ? 'true' : 'false'}>
              <span style={bs.label} dir="auto">{p.user_id === me ? t('board.you') : (p.name || t('board.someone'))}</span>
              {bar(p.score)}
              <span style={bs.value}>{Number(p.score).toFixed(1)}</span>
            </li>
          ))}
        </ol>
      </section>
      {board.best && board.best.score > 0 && (
        <p data-testid="event-board-best">
          {t('board.best', { score: Number(board.best.score).toFixed(1),
            name: cardsById[board.best.card_id]?.author_name || t('board.someone') })}
        </p>
      )}
      {board.crowd_favorite?.length > 0 && (
        <section aria-labelledby="board-crowd">
          <h3 id="board-crowd" style={bs.h}>{t('board.crowd')}</h3>
          <ol style={bs.list}>
            {board.crowd_favorite.map((c) => (
              <li key={c.card_id} data-testid="event-board-crowd">
                {cardsById[c.card_id]?.author_name || t('board.someone')} · {t('board.votes', { count: c.votes })}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

const bs = {
  h: { margin: '0 0 6px', fontSize: 15 },
  list: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 },
  row: { display: 'grid', gridTemplateColumns: 'minmax(80px, 140px) 1fr 44px', gap: 8, alignItems: 'center' },
  label: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  track: { display: 'block', height: 10, borderRadius: 5, background: 'var(--c-border)' },
  fill: { display: 'block', height: '100%', borderRadius: 5 },
  value: { textAlign: 'end', fontVariantNumeric: 'tabular-nums' },
};
